import React from 'react';
import { formatDuration } from '../../../../utils/utils';
import { TARGET_AGE_MAP } from '../../../../utils/META/cat_109';
import { DQC_COLORS, KSAB_COLOR_MAPPING } from '../../../../utils/constants';
import PoweredByDQ from '../../../../assets/images/PoweredByDQ_Default.png';
import { tUi } from '../../../../utils/dq_lc';

const getDqcoColor = (dqco, dqcoList) => {
    if(!dqcoList) return '#444';
    const obj = dqcoList.find(item => item.dqco_no === dqco);
    if(!obj) return '#444';
    return DQC_COLORS[obj.dqc_no] || '#444';
};

const getDqcoName = (dqco, dqcoList) => {
    if(!dqcoList) return '';
    const obj = dqcoList.find(item => item.dqco_no === dqco);
    return obj?.short_name || obj?.full_name || '';
};

const OutlineHtmlDoc = React.forwardRef(({
    programData,
    programAudience,
    programDuration,
    modules = [],
    isEducatorView,
    dqcoList,
    uiLocale
}, ref) => {
    const totalModules = modules.length;
    const totalUnits = modules.reduce((acc, m) => acc + (m?.units?.length || 0), 0);
    const totalSessions = modules.reduce(
        (acc, m) => acc + (m?.units || []).reduce((a, u) => a + (u?.lessons?.length || 0), 0),
        0
    );

    const learnersText = Array.isArray(programAudience)
        ? programAudience.map((v) => TARGET_AGE_MAP[v]?.label || v).join(", ")
        : TARGET_AGE_MAP[programAudience]?.label || programAudience || tUi('outlineDoc.meta.notSpecified', uiLocale);

    // Collect all unique frameworks from all modules
    const getAllFrameworks = () => {
        const DEFAULT_FRAMEWORK_NAME = 'IEEE DQ Global Standards (IEEE3527.1)';
        const defaultEntry = {
            FrameName: DEFAULT_FRAMEWORK_NAME,
            "SourceLink (same as ProgL2)": 'https://www.dqinstitute.org/global-standards/'
        };

        const frameworksMap = new Map();
        frameworksMap.set(DEFAULT_FRAMEWORK_NAME, defaultEntry);

        modules.forEach(module => {
            if (Array.isArray(module.unique_frame_arc)) {
                module.unique_frame_arc.forEach(framework => {
                    const frameName = framework?.FrameName?.trim();
                    if (frameName && frameName !== DEFAULT_FRAMEWORK_NAME && !frameworksMap.has(frameName)) {
                        frameworksMap.set(frameName, framework);
                    }
                });
            }
        });

        const allFrameworks = Array.from(frameworksMap.values());
        const defaultFramework = allFrameworks.shift();
        const sorted = allFrameworks.sort((a, b) => {
            const aName = String(a?.FrameName ?? '').toLowerCase();
            const bName = String(b?.FrameName ?? '').toLowerCase();
            return aName.localeCompare(bName);
        });

        return [defaultFramework, ...sorted];
    };

    const allFrameworks = getAllFrameworks();

    const styles = `
        @media print {
            @page { size: A4; margin: 0; }
            body { margin: 0; padding: 0; }
            .pdf-container {
                padding: 60px 50px 50px 50px;
                -webkit-box-decoration-break: clone;
                box-decoration-break: clone;
            }
        }

        .pdf-container {
            font-family: 'Noto Sans KR', 'Noto Sans JP', 'Noto Sans Thai', Arial, sans-serif;
            font-size: 10pt;
            line-height: 1.5;
            color: #000;
            background: white;
            max-width: 800px;
            margin: 0 auto;
            padding: 80px 70px 72px 54px;
        }

        .header {
            text-align: right;
            margin-bottom: 10px;
        }

        .header img {
            height: 45px;
            margin-left: 6px;
            margin-top: 0;
            margin-bottom: 20px;
        }

        .subtitle-top {
            font-size: 13pt;
            text-align: center;
            margin-top: 10px;
            margin-bottom: 2px;
        }

        .title {
            font-size: 18pt;
            font-weight: bold;
            text-align: center;
            margin-top: 4px;
            margin-bottom: 4px;
        }

        .view-type {
            font-size: 13pt;
            text-align: center;
            margin-top: 2px;
            margin-bottom: 8px;
            font-style: italic;
        }

        .meta-section {
            margin-top: 10px;
            margin-bottom: 8px;
        }

        .meta-line {
            font-size: 12pt;
            text-align: left;
            margin-bottom: 2px;
        }

        .meta-label {
            font-weight: bold;
        }

        .section-title {
            font-size: 14pt;
            font-weight: bold;
            margin-top: 12px;
            margin-bottom: 4px;
        }

        .overview-text {
            font-size: 12pt;
        }

        .ksab-pill {
            align-self: center;
            background-color: #f3f3f3;
            border-radius: 12px;
            padding: 18px 13px;
            margin-top: 13px;
            margin-bottom: 15px;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
        }

        .ksab-pill-label {
            font-size: 11pt;
            font-weight: bold;
            margin-right: 10px;
        }

        .ksab-pill-item {
            display: flex;
            align-items: center;
            margin-right: 14px;
        }

        .ksab-dot-lg {
            width: 11px;
            height: 11px;
            border-radius: 5px;
            margin-right: 6px;
        }

        .module-divider {
            height: 0.8px;
            background-color: #000;
            width: 100%;
            margin: 10px auto;
            border-radius: 1px;
        }

        .unit-divider {
            height: 0.6px;
            background-color: #d9d9d9;
            width: 77%;
            margin: 12px auto;
            border-radius: 1px;
        }

        .module-block {
            margin-bottom: 12px;
        }

        .module-title {
            font-size: 14pt;
            font-weight: bold;
        }

        .module-description {
            margin-bottom: 10px;
            font-size: 12pt;
        }

        .dqco-section {
            margin-top: 6px;
        }

        .dqco-label {
            font-size: 11pt;
            font-style: italic;
            font-weight: bold;
            margin-bottom: 2px;
            margin-top: 2px;
        }

        .tag-wrap {
            display: flex;
            flex-wrap: wrap;
            margin-top: 4px;
            margin-bottom: 4px;
        }

        .tag {
            background-color: #e6f1fe;
            border-radius: 14px;
            padding: 5px 8px;
            margin-right: 6px;
            margin-bottom: 6px;
            max-width: 220px;
        }

        .tag-text {
            font-size: 6pt;
            color: #fff;
            font-weight: bold;
            text-align: center;
            white-space: pre-line;
        }

        .unit-block {
            margin-top: 8px;
            margin-left: 10px;
            padding-left: 8px;
        }

        .unit-title-band {
            border-radius: 4px;
            padding: 4px 6px;
            margin-bottom: 4px;
            display: inline-block;
        }

        .unit-title-text {
            font-size: 14pt;
            padding: 0 6px;
            font-weight: bold;
        }

        .unit-description {
            padding-left: 13px;
            font-size: 12pt;
        }

        .session-block {
            margin-top: 14px;
            margin-left: 26px;
            padding-left: 18px;
            padding-right: 18px;
        }

        .session-header {
            font-size: 14pt;
            font-weight: bold;
        }

        .session-meta {
            margin-top: 2px;
            margin-bottom: 3px;
            font-size: 12pt;
        }

        .ksab-row {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            margin-top: 13px;
            margin-bottom: 13px;
        }

        .ksab-row-label {
            font-size: 11pt;
            font-weight: bold;
            margin-right: 6px;
        }

        .ksab-dot {
            width: 9px;
            height: 9px;
            border-radius: 5px;
            margin-right: 1px;
        }

        .objectives-section {
            margin-top: 2px;
        }

        .objectives-title {
            font-size: 13pt;
            font-weight: bold;
            margin-bottom: 2px;
            page-break-after: avoid;
        }

        .objective-item {
            display: flex;
            margin-left: 20px;
            margin-bottom: 6px;
        }

        .bullet {
            margin-right: 8px;
            font-size: 25pt;
            font-weight: bold;
        }

        .bullet-text {
            font-size: 12pt;
            margin-left: 10px;
            margin-bottom: 3px;
        }

        .duration {
            font-size: 9pt;
            color: #000;
        }

        .frameworks-page {
            page-break-before: always;
            padding-top: 20px;
            padding-bottom: 72px;
            padding-left: 54px;
            padding-right: 70px;
        }

        .frameworks-list {
            margin-top: 10px;
            padding-left: 20px;
        }

        .framework-item {
            display: flex;
            margin-bottom: 4px;
            padding-left: 10px;
        }

        .framework-bullet {
            font-size: 20pt;
            margin-right: 10px;
            font-weight: bold;
        }

        .framework-link {
            font-size: 12pt;
            color: #0066cc;
            text-decoration: underline;
        }

        .framework-text {
            font-size: 12pt;
            color: #000;
        }

        .no-frameworks {
            font-size: 12pt;
            color: #666;
            font-style: italic;
            text-align: center;
            margin-top: 20px;
        }

        .footer {
            margin-top: 40px;
            padding-top: 20px;
            text-align: center;
            font-size: 8pt;
            color: #444;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
        }

        .circle-icon {
            width: 9px;
            height: 9px;
            border: 1px solid #444;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 9pt;
            margin: 0 2px;
        }
    `;

    return (
        <div ref={ref}>
            <style>{styles}</style>

            {/* Main Page */}
            <div className="pdf-container">
                {/* Header */}
                <div className="header">
                    <img src={PoweredByDQ} alt="Powered by DQ" />
                </div>

                {/* Title Block */}
                <div className="subtitle-top">{tUi('outlineDoc.title.prefix', uiLocale)}</div>
                <div className="title">{programData?.full_name}</div>
                <div className="view-type">
                    ({isEducatorView ? tUi('outlineDoc.viewType.educator', uiLocale) : tUi('outlineDoc.viewType.learner', uiLocale)})
                </div>

                {/* Meta Information */}
                <div className="meta-section">
                    <div className="meta-line">
                        <span className="meta-label">{tUi('outlineDoc.meta.learners', uiLocale)} </span>{learnersText}
                    </div>
                    <div className="meta-line">
                        <span className="meta-label">{tUi('outlineDoc.meta.programStructure', uiLocale)} </span>
                        {totalModules} {tUi('outlineDoc.structure.modules', uiLocale)} / {totalUnits} {tUi('outlineDoc.structure.units', uiLocale)} / {totalSessions} {tUi('outlineDoc.structure.sessions', uiLocale)}
                    </div>
                    <div className="meta-line">
                        <span className="meta-label">{tUi('outlineDoc.meta.programLength', uiLocale)} </span>{formatDuration(programDuration, uiLocale)}
                    </div>
                </div>

                {/* Overview */}
                {programData?.description && (
                    <>
                        <div className="section-title">{tUi('outlineDoc.section.overview', uiLocale)}</div>
                        <div className="overview-text">{programData.description}</div>
                    </>
                )}

                {/* KSAB Legend Pill */}
                <div className="ksab-pill">
                    <span className="ksab-pill-label">{tUi('outlineDoc.ksab.label', uiLocale)}</span>
                    {[
                        { key: 'Knowledge', label: tUi('outlineDoc.ksab.knowledge', uiLocale) },
                        { key: 'Skills', label: tUi('outlineDoc.ksab.skills', uiLocale) },
                        { key: 'Attitudes', label: tUi('outlineDoc.ksab.attitudes', uiLocale) },
                        { key: 'Behaviours', label: tUi('outlineDoc.ksab.behaviours', uiLocale) }
                    ].map(({ key, label }) => (
                        <div key={key} className="ksab-pill-item">
                            <div className="ksab-dot-lg" style={{ backgroundColor: KSAB_COLOR_MAPPING[key] || "#bbb" }}></div>
                            <span style={{ fontSize: '9pt' }}>{label}</span>
                        </div>
                    ))}
                </div>

                {/* Modules / Units / Sessions */}
                {modules.map((mod, mIdx) => {
                    const moduleDescription = isEducatorView ? mod.module_educator_description : mod.module_learner_description;

                    return (
                        <div key={mIdx} className="module-block">
                            <div className="module-divider"></div>
                            <div className="section-title">
                                <span>{tUi('outlineDoc.module.label', uiLocale)} {mIdx + 1}: {mod.module_name} </span>
                                <span className="duration">({formatDuration(mod.module_duration, uiLocale)})</span>
                            </div>
                            {moduleDescription && (
                                <div className="module-description">{moduleDescription}</div>
                            )}

                            {/* DQ Code chips */}
                            {mod.unique_dqco?.length > 0 && (
                                <div className="dqco-section">
                                    <div className="dqco-label">{tUi('outlineDoc.dqCode.label', uiLocale)}</div>
                                    <div className="tag-wrap">
                                        {mod.unique_dqco.map((code, i) => {
                                            const name = getDqcoName(code, dqcoList);
                                            const color = getDqcoColor(code, dqcoList);
                                            return (
                                                <div key={`${code}-${i}`} className="tag" style={{ backgroundColor: color }}>
                                                    <div className="tag-text">{name ? `${code}\n${name}` : code}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Units */}
                            {(mod.units || []).map((u, uIdx) => {
                                const unitDescription = isEducatorView ? u.unit_educator_description : u.unit_learner_description;

                                return (
                                    <div key={uIdx} className="unit-block">
                                        <div className="unit-divider"></div>
                                        <div className="unit-title-band">
                                            <span className="unit-title-text">
                                                {tUi('outlineDoc.unit.label', uiLocale)} {uIdx + 1}: {u.unit_title} <span className="duration">({formatDuration(u.unit_duration, uiLocale)})</span>
                                            </span>
                                        </div>
                                        {unitDescription && (
                                            <div className="unit-description">{unitDescription}</div>
                                        )}

                                        {/* Sessions */}
                                        {(u.lessons || []).map((s, sIdx) => {
                                            const ksabTokens = s.session_unique_ksab || [];
                                            const objectiveItems = (s.session_learning_objectives || [])
                                                .map((o) => o.description_for_educator || o.description_for_learner || o.description)
                                                .filter(Boolean);
                                            const sessionDescription = isEducatorView ? s.lesson_educator_description : s.lesson_learner_description;

                                            return (
                                                <div key={sIdx} className="session-block">
                                                    <div className="session-header">
                                                        {tUi('outlineDoc.session.label', uiLocale)} {sIdx + 1}: {s.lesson_title} <span className="duration">({formatDuration(s.lesson_duration, uiLocale)})</span>
                                                    </div>

                                                    {sessionDescription && (
                                                        <div className="session-meta">{sessionDescription}</div>
                                                    )}

                                                    {ksabTokens.length > 0 && (
                                                        <div className="ksab-row">
                                                            <span className="ksab-row-label">KSAB:</span>
                                                            {ksabTokens.map((t, i) => (
                                                                <div key={`${t}-${i}`} style={{ display: 'flex', alignItems: 'center', marginRight: '1px' }}>
                                                                    <div className="ksab-dot" style={{ backgroundColor: KSAB_COLOR_MAPPING[t] || "#bbb" }}></div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {objectiveItems.length > 0 && (
                                                        <div className="objectives-section">
                                                            <div className="objectives-title">{tUi('outlineDoc.session.learningObjectives', uiLocale)}</div>
                                                            {objectiveItems.map((line, idx) => (
                                                                <div key={idx} className="objective-item">
                                                                    <span className="bullet">•</span>
                                                                    <span className="bullet-text">{line}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}

                {/* Footer */}
                <div className="footer">
                    <span className="circle-icon">C</span>
                    <span> {new Date().getFullYear()} {tUi('outlineDoc.footer.copyright', uiLocale)}</span>
                    <span className="circle-icon">R</span>
                    <span> {tUi('outlineDoc.footer.trademark', uiLocale)}</span>
                </div>
            </div>

            {/* Frameworks Aligned Page */}
            <div className="frameworks-page">
                {/* Header */}
                <div className="header">
                    <img src={PoweredByDQ} alt="Powered by DQ" />
                </div>

                {/* Title */}
                <div className="section-title">{tUi('outlineDoc.frameworksAligned', uiLocale)}</div>

                {/* Frameworks List */}
                {allFrameworks && allFrameworks.length > 0 ? (
                    <div className="frameworks-list">
                        {allFrameworks.map((framework, index) => (
                            <div key={index} className="framework-item">
                                <span className="framework-bullet">•</span>
                                {framework["SourceLink (same as ProgL2)"] ? (
                                    <a href={framework["SourceLink (same as ProgL2)"]} target="_blank" rel="noopener noreferrer" className="framework-link">
                                        {framework.FrameName}
                                    </a>
                                ) : (
                                    <span className="framework-text">{framework.FrameName}</span>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="no-frameworks">{tUi('outlineDoc.noFrameworks', uiLocale)}</div>
                )}

                {/* Footer */}
                <div className="footer">
                    <span className="circle-icon">C</span>
                    <span> {new Date().getFullYear()} {tUi('outlineDoc.footer.copyright', uiLocale)}</span>
                    <span className="circle-icon">R</span>
                    <span> {tUi('outlineDoc.footer.trademark', uiLocale)}</span>
                </div>
            </div>
        </div>
    );
});

OutlineHtmlDoc.displayName = 'OutlineHtmlDoc';

export default OutlineHtmlDoc;
